/**
 * Intent-based early mount for CommonModals.
 * Keeps the global idle gate intact; only bypasses it when a lead CTA
 * is about to open (hover / near-viewport / click).
 */

import type { ComponentType } from "react";
import type { ModalTypes } from "@/types/modal";
import {
  ensureModalChunk,
  isModalChunkReady,
  warmHotModalChunks,
} from "@/components/sections/CommonModals/modalLoaders";

type Listener = () => void;
type HostComponent = ComponentType<{ isMobile: boolean }>;

const listeners = new Set<Listener>();
const hostListeners = new Set<Listener>();
let forced = false;
let hostReady = false;
let hostInflight: Promise<unknown> | null = null;
let HostComponentRef: HostComponent | null = null;

export function areLeadModalsForced(): boolean {
  return forced;
}

export function forceLeadModalsLoad(): void {
  if (forced) return;
  forced = true;
  listeners.forEach((listener) => listener());
}

export function subscribeLeadModalsForce(listener: Listener): () => void {
  listeners.add(listener);
  if (forced) listener();
  return () => {
    listeners.delete(listener);
  };
}

export function getLeadModalHost(): HostComponent | null {
  return HostComponentRef;
}

export function subscribeLeadModalHost(listener: Listener): () => void {
  hostListeners.add(listener);
  if (HostComponentRef) listener();
  return () => {
    hostListeners.delete(listener);
  };
}

function ensureLeadModalHost(): Promise<unknown> {
  if (typeof window === "undefined") return Promise.resolve();
  if (hostReady && HostComponentRef) return Promise.resolve();
  if (!hostInflight) {
    hostInflight = Promise.all([
      import("@sections/CommonModals"),
      import("@/components/atoms/FeeScholorshipRFI/FeeScholorshipRFI"),
    ])
      .then(([mod]) => {
        HostComponentRef = (mod as { default: HostComponent }).default;
        hostReady = true;
        hostInflight = null;
        hostListeners.forEach((listener) => listener());
      })
      .catch((err) => {
        hostInflight = null;
        throw err;
      });
  }
  return hostInflight;
}

export function isLeadModalHostReady(): boolean {
  return hostReady && !!HostComponentRef;
}

/** True when host + body can paint immediately with no loading gap. */
export function canOpenLeadModalSync(
  modalType?: ModalTypes | null,
): boolean {
  return isLeadModalHostReady() && isModalChunkReady(modalType);
}

/**
 * Prefetch modal host + optional body chunk.
 * When modalType is set (CTA click), loads that body in parallel with the host
 * so OpenModal can wait for both and paint fully formed (no flicker).
 * When called without a type (hover warm), also warms high-traffic bodies.
 */
export function warmLeadModals(modalType?: ModalTypes | null): void {
  forceLeadModalsLoad();
  if (typeof window === "undefined") return;
  void ensureLeadModalHost().catch(() => {});
  if (modalType) {
    void ensureModalChunk(modalType).catch(() => {});
  } else {
    warmHotModalChunks();
  }
}

/**
 * Resolve host + body chunks before opening so the dialog never flashes empty.
 * Cached after first load → subsequent opens stay synchronous.
 */
export async function prepareLeadModalOpen(
  modalType?: ModalTypes | null,
): Promise<void> {
  forceLeadModalsLoad();
  if (typeof window === "undefined") return;

  const tasks: Promise<unknown>[] = [ensureLeadModalHost()];
  if (modalType) {
    tasks.push(ensureModalChunk(modalType));
  }

  try {
    await Promise.all(tasks);
  } catch {
    // Open anyway — better a late form than a dead CTA.
  }
}

export { isModalChunkReady, ensureModalChunk };
